/**
 * Sync Conflict Resolution Modal
 * Allows users to resolve data conflicts when syncing offline changes
 */

import React, { useState } from 'react';
import { AlertTriangle, Check, X, GitMerge, ArrowRight } from 'lucide-react';
import { useConflicts } from '@/lib/offline/hooks';
import { SyncConflict } from '@/lib/offline/types';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface SyncConflictModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SyncConflictModal({ open, onOpenChange }: SyncConflictModalProps) {
  const { conflicts, resolveConflict, hasConflicts } = useConflicts();
  const { fmt } = useFormattedDate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolving, setResolving] = useState(false);

  // Auto-open when conflicts exist
  const isOpen = open ?? hasConflicts;

  const currentConflict = conflicts[currentIndex];

  const handleResolve = async (resolution: 'client' | 'server') => {
    if (!currentConflict) return;

    setResolving(true);
    try {
      await resolveConflict(currentConflict.id, resolution);

      // Move to next conflict or close
      if (currentIndex >= conflicts.length - 1) {
        setCurrentIndex(0);
        onOpenChange?.(false);
      }
    } finally {
      setResolving(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return fmt.dateTime(timestamp);
  };

  const formatEntityType = (type: string) => {
    return type
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (!currentConflict) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Sync Conflict Detected
          </DialogTitle>
          <DialogDescription>
            Your local changes conflict with changes made on the server.
            Choose which version to keep.
          </DialogDescription>
        </DialogHeader>

        {conflicts.length > 1 && (
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm text-muted-foreground">
              Conflict {currentIndex + 1} of {conflicts.length}
            </span>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setCurrentIndex(Math.min(conflicts.length - 1, currentIndex + 1))
                }
                disabled={currentIndex === conflicts.length - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Entity info */}
          <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
            <div>
              <span className="text-sm text-muted-foreground">Entity Type</span>
              <p className="font-medium">
                {formatEntityType(currentConflict.entityType)}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">ID</span>
              <p className="font-mono text-sm">{currentConflict.entityId}</p>
            </div>
          </div>

          {/* Comparison view */}
          <Tabs defaultValue="side-by-side" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
              <TabsTrigger value="diff">Differences</TabsTrigger>
            </TabsList>

            <TabsContent value="side-by-side" className="mt-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Your Version */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Badge variant="secondary">Your Version</Badge>
                    </h4>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(currentConflict.clientTimestamp)}
                    </span>
                  </div>
                  <ScrollArea className="h-64 rounded-md border p-3">
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(currentConflict.clientData, null, 2)}
                    </pre>
                  </ScrollArea>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleResolve('client')}
                    disabled={resolving}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Keep My Version
                  </Button>
                </div>

                {/* Server Version */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <Badge>Server Version</Badge>
                    </h4>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(currentConflict.serverTimestamp)}
                    </span>
                  </div>
                  <ScrollArea className="h-64 rounded-md border p-3">
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(currentConflict.serverData, null, 2)}
                    </pre>
                  </ScrollArea>
                  <Button
                    className="w-full"
                    onClick={() => handleResolve('server')}
                    disabled={resolving}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Keep Server Version
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="diff" className="mt-4">
              <ConflictDiff conflict={currentConflict} />
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            className="sm:mr-auto"
          >
            Resolve Later
          </Button>

          {conflicts.length > 1 && (
            <Button
              variant="outline"
              onClick={async () => {
                // Resolve all with server version
                for (const conflict of conflicts) {
                  await resolveConflict(conflict.id, 'server');
                }
                onOpenChange?.(false);
              }}
              disabled={resolving}
            >
              <ArrowRight className="h-4 w-4 mr-2" />
              Accept All Server Changes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Diff view component
 */
function ConflictDiff({ conflict }: { conflict: SyncConflict }) {
  const clientKeys = Object.keys(conflict.clientData as object);
  const serverKeys = Object.keys(conflict.serverData as object);
  const allKeys = [...new Set([...clientKeys, ...serverKeys])];

  const clientData = conflict.clientData as Record<string, unknown>;
  const serverData = conflict.serverData as Record<string, unknown>;

  return (
    <ScrollArea className="h-80">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left p-2 font-medium">Field</th>
            <th className="text-left p-2 font-medium">Your Version</th>
            <th className="text-left p-2 font-medium">Server Version</th>
          </tr>
        </thead>
        <tbody>
          {allKeys.map((key) => {
            const clientValue = clientData[key];
            const serverValue = serverData[key];
            const isDifferent =
              JSON.stringify(clientValue) !== JSON.stringify(serverValue);

            return (
              <tr
                key={key}
                className={cn(
                  'border-b',
                  isDifferent && 'bg-amber-50 dark:bg-amber-950/20'
                )}
              >
                <td className="p-2 font-mono text-xs">{key}</td>
                <td className="p-2">
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    {formatValue(clientValue)}
                  </code>
                </td>
                <td className="p-2">
                  <code className="text-xs bg-muted px-1 py-0.5 rounded">
                    {formatValue(serverValue)}
                  </code>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollArea>
  );
}

function formatValue(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Conflict notification badge
 */
export function ConflictBadge({ className }: { className?: string }) {
  const { conflicts, hasConflicts } = useConflicts();

  if (!hasConflicts) return null;

  return (
    <Badge
      variant="destructive"
      className={cn('flex items-center gap-1', className)}
    >
      <AlertTriangle className="h-3 w-3" />
      {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''}
    </Badge>
  );
}

export default SyncConflictModal;
